import { useEffect, useState, useMemo, useCallback } from "react";
import { FormattedMessage } from "react-intl";
import { SelectedService } from "../../../modification/model";
import { messages } from "./messages";

interface Props {
  extra: SelectedService;
  onSubmit: (updatedService: SelectedService) => void;
  onClose: () => void;
}

export function ExtraPriceModification({ extra, onSubmit, onClose }: Props) {
  const [newRates, setNewRates] = useState<Record<string, number>>({});
  const [focusedInput, setFocusedInput] = useState<string | null>(null);
  const [rawInputValues, setRawInputValues] = useState<Record<string, string>>({});

  const getUnitTypeMessage = () => {
    if (extra.type.includes("PERSON")) {
      return messages.perPerson;
    }
    if (extra.type.includes("UNIT")) {
      return messages.perUnit;
    }
    return messages.perRoom;
  };

  useEffect(() => {
    const init: Record<string, number> = {};
    Object.keys(extra.dateQuantity || {}).forEach(date => {
      if (extra.totalRatePerDay?.[date] !== undefined) {
        init[date] = extra.totalRatePerDay[date];
      } else {
        init[date] = extra.quantity > 0 ? extra.amount.amount / extra.quantity : 0;
      }
    });
    setNewRates(init);
  }, [extra]);

  const handleRateChange = useCallback(
    (date: string, value: number) => setNewRates(prev => ({ ...prev, [date]: value })),
    []
  );

  const dates = useMemo(() => Object.keys(extra.dateQuantity || {}).sort(), [extra.dateQuantity]);

  const isSingleDate = useMemo(() => Object.keys(extra.dateQuantity || {}).length === 1, [
    extra.dateQuantity,
  ]);

  const showAllDates = useMemo(
    () => extra.dateSelectionType === "SPECIFIC" || extra.dateSelectionType === "ALL",
    [extra.dateSelectionType]
  );

  const displayDates = useMemo(() => (showAllDates ? dates : dates.slice(0, 1)), [
    showAllDates,
    dates,
  ]);

  const handleRateInputChange = useCallback((date: string, value: string) => {
    if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
      setRawInputValues(prev => ({ ...prev, [date]: value }));
    }
  }, []);

  const handleInputFocus = useCallback((date: string) => {
    setFocusedInput(date);
  }, []);

  const handleInputBlur = useCallback(
    (date: string) => {
      setFocusedInput(null);
      const rawValue = rawInputValues[date];
      if (rawValue !== undefined) {
        handleRateChange(date, Number(rawValue || 0));
      }
      setRawInputValues(prev => {
        const copy = { ...prev };
        delete copy[date];
        return copy;
      });
    },
    [rawInputValues, handleRateChange]
  );

  const getInputValue = useCallback(
    (date: string) => {
      const isFocused = focusedInput === date;
      const rate = newRates[date];

      if (isFocused) {
        return rawInputValues[date] ?? rate?.toString() ?? "";
      }

      return rate !== undefined ? rate.toFixed(2) : "";
    },
    [focusedInput, newRates, rawInputValues]
  );

  const getQuantityForDate = useCallback(
    (date: string) => {
      return isSingleDate ? extra.quantity : extra.dateQuantity?.[date] ?? 0;
    },
    [isSingleDate, extra.dateQuantity, extra.quantity]
  );

  const getAppliedRateForDate = useCallback(
    (date: string) => {
      if (extra.totalRatePerDay?.[date] !== undefined) {
        return extra.totalRatePerDay[date].toFixed(2);
      }

      if (extra.quantity > 0) {
        return (extra.amount.amount / extra.quantity).toFixed(2);
      }

      return "0";
    },
    [extra.totalRatePerDay, extra.quantity, extra.amount.amount]
  );

  const handleApplyToAll = useCallback(() => {
    if (dates.length === 0) {
      return;
    }
    const firstRate = newRates[dates[0]] || 0;
    const updated = Object.fromEntries(dates.map(day => [day, firstRate]));
    setNewRates(updated);
  }, [dates, newRates]);

  const total = useMemo(() => {
    return displayDates.reduce((sum, date) => {
      const quantity = isSingleDate ? extra.quantity : extra.dateQuantity?.[date] || 0;
      return sum + quantity * (newRates[date] || 0);
    }, 0);
  }, [isSingleDate, extra.quantity, extra.dateQuantity, displayDates, newRates]);

  const handleSubmit = useCallback(() => {
    onSubmit({
      ...extra,
      amount: {
        ...extra.amount,
        amount: total,
      },
      totalRatePerDay: { ...newRates },
      dateQuantity: { ...extra.dateQuantity },
    });
  }, [extra, newRates, total, onSubmit]);

  const baseCellStyles =
    "min-h-[25px] flex align-items-center justify-content-center h-full border-r border-gray-300 px-2";
  const cellStyles = (withBg = false) =>
    withBg ? `${baseCellStyles} bg-gray-100` : baseCellStyles;
  const headerCellStyles =
    "flex align-items-center justify-content-center border-r border-gray-300 px-2 py-3";

  return (
    <div className="text-[#2E3862] font-semibold">
      <h2 className="text-[18px] font-semibold mb-1">{extra.name}</h2>
      <div className="text-[13px]">
        <FormattedMessage {...getUnitTypeMessage()} />
      </div>

      <div className="py-[30px] text-[13px]">
        <div className="grid grid-cols-5 font-semibold">
          <div className={`${headerCellStyles} border-t border-l border-b border-gray-300`}>
            <FormattedMessage {...messages.date} />
          </div>
          <div className={`${headerCellStyles} border-t border-b border-gray-300`}>
            <FormattedMessage {...messages.units} />
          </div>
          <div className={`${headerCellStyles} border-t border-b border-gray-300`}>
            <FormattedMessage {...messages.appliedRatePerExtra} />
          </div>
          <div className={`${headerCellStyles} border-t border-b border-gray-300`}>
            <FormattedMessage {...messages.newAppliedRate} />
          </div>
          <div className="px-2 py-3"></div>
        </div>

        {displayDates.map((date, index) => (
          <div key={date} className="grid grid-cols-5 items-center ">
            <div className={`${cellStyles()} border-l border-b border-gray-300`}>{date}</div>
            <div className={`${cellStyles()} border-b border-gray-300`}>
              {getQuantityForDate(date)}
            </div>
            <div className={`${cellStyles(true)} border-b border-gray-300`}>
              {getAppliedRateForDate(date)}
            </div>
            <div className={`${cellStyles()} border-b border-gray-300 text-gray-500`}>
              <input
                type="number"
                value={getInputValue(date)}
                onChange={event => handleRateInputChange(date, event.target.value)}
                onFocus={() => handleInputFocus(date)}
                onBlur={() => handleInputBlur(date)}
                className="w-full text-center"
              />
            </div>
            <div className="px-2 min-h-[25px] flex align-items-center justify-content-center">
              {index === 0 && dates.length > 1 && showAllDates && (
                <div
                  onClick={handleApplyToAll}
                  className="text-gray-500 cursor-pointer text-uppercase">
                  <FormattedMessage {...messages.applyToAll} />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="mb-6">
        <div className="text-base font-bold">
          <FormattedMessage {...messages.total} />: {total.toFixed(2)}{" "}
          {extra.amount.currency.toUpperCase()}
        </div>
        <div className="text-sm font-weight-normal mt-1">
          <FormattedMessage {...messages.extraTotalNote} />
        </div>
      </div>
      <div className="flex items-center justify-center gap-4">
        <div onClick={handleSubmit} className="btn btn-primary px-8 py-2">
          <FormattedMessage {...messages.confirm} />
        </div>
        <button onClick={onClose} className="btn btn-secondary px-8 py-2">
          <FormattedMessage {...messages.cancel} />
        </button>
      </div>
    </div>
  );
}
